// THE UNIT THE OWNER TYPED INSIDE THE ADDRESS LINE, LIFTED INTO THE UNIT FIELD (R115, Tyler, 2026-09-16).
//
// *"The unit lift applies to BOTH clients, one parser."* The parser is the app's, this file is its port, and it is
// PORTED rather than rewritten: the vocabulary and the plausible-value rule below are `AddressMatch`'s own, copied
// character for character and pinned to the Swift source by functions/test/v2/resolve/recordPins.test.ts, section 5.
//
// ── WHICH PARSER, AND THE MEASUREMENT THAT DECIDES IT ───────────────────────────────────────────────────────────────
//
// `AppealAce/Services/PropertyDataProvider.swift` holds TWO, deliberately, and R115's own evidence line names the wrong
// one. They are not interchangeable and Swift's comments say so:
//
//   `subUnitWords` / `subUnitDesignator(in:)` (:861, :697)   THE LOT VOCABULARY — {LOT, SPC, SPACE, TRLR, TRAILER},
//       value must contain a DIGIT. Already ported, in ./recordMatch.ts, for the refusal it drives. Swift: "`UNIT`,
//       `APT` and `#` ARE DELIBERATELY ABSENT, and this is the important part of the set." So this parser lifts
//       NOTHING out of `226 W Rittenhouse Sq Unit 1512` — the fixture R115 is ruled on.
//   `unitDesignatorWords` / `typedUnitDesignator(in:)` (:585, :597)   THE CONDOMINIUM VOCABULARY, and the function
//       whose whole job is "a unit designator the typed text names". Swift: "Deliberately a SECOND set, not an
//       addition to `subUnitWords` below." This is the parser R115 describes, and this file is its port.
//
// SO THE RULE PORTED HERE IS NOT A DIGIT RULE. It is `plausibleValue`: non-empty, at most eight letters-and-digits
// after everything else is stripped, and nothing in it but letters, digits, `-` and `#`. Swift's comment gives the
// measurement it exists for — "the length cap is what stops a missing comma turning 'Apt 2210 Philadelphia' into the
// unit 'Philadelphia'". A digit is NOT required, and requiring one here would be a WIDENING of the refusal in the
// direction block-versus-degrade forbids: `Apt B`, `PH2` and `Unit F` are real units, `Models.swift:961` says so
// ("letter-only units are real and already supported end to end"), and `unitRequirementUnmet` deliberately applies no
// digit rule for the same reason. R115: "Do not widen or 'improve' the rule on either side."
//
// WHAT THAT COSTS, STATED RATHER THAN DISCOVERED LATER: a street genuinely named "Unit Avenue" would have "Unit Ave"
// read as a unit, which is the shape `subUnitDesignator`'s digit rule exists for on the LOT side ("100 Lot Ave"). It is
// ported unchanged because porting it unchanged is the ruling, because the app has shipped this exact rule on the
// overwrite path since R43, and because the lift is VISIBLE and EDITABLE: the value travels on the resolve response and
// the owner reads it at the pre-spend confirmation before any money moves. A wrong lift is a question on a screen; a
// narrowed rule is a condominium owner blocked at the gate.
//
// ── LOOSE ON NIL, AND THE OWNER'S OWN FIELD ALWAYS WINS ─────────────────────────────────────────────────────────────
//
// A typed line naming no designator lifts nothing and the unit stays null. A unit field the owner filled is returned
// VERBATIM and the address line is not even parsed — `SubjectProperty.unitNumber`'s one writer is the owner
// (F-W2.4c-3), and the lift adds a second writer only where the first one said nothing. "Said nothing" is
// `trimmed(...)` empty, which is exactly `unitRequirementUnmet`'s own test, so the lift can never disagree with G-4
// about whether the owner answered.
//
// Pure: no clock, no I/O.

import { characters, isSwiftLetter, isSwiftNumber, trimmed, uppercased } from "../valuation/format";
import { bareUnitToken } from "./unitAddress";

/** `AddressMatch.unitDesignatorWords` (PropertyDataProvider.swift:612), character for character.
 *
 *  A FOURTH COPY of a unit vocabulary in this repository, and the three it is NOT are worth naming, because picking the
 *  wrong one is the defect this file was nearly written with:
 *    `SUB_UNIT_WORDS` (./recordMatch.ts)          the LOT set — {LOT, SPC, SPACE, TRLR, TRAILER}.
 *    `UNIT_DESIGNATOR_WORDS` (./unitAddress.ts)   what goes INTO a lookup address, and what v1's realie.ts reads back
 *                                                 out of one; fifteen words, LOT and TRLR among them.
 *    `UNIT_DESIGNATORS` (identity/addressNormalize.ts)  USPS Pub 28 canonicalization, designators mapped to one spelling.
 *  This set is none of those: eleven words, no lot vocabulary, and it names what a person TYPES on an address line.
 *  Pinned to the Swift by functions/test/v2/resolve/recordPins.test.ts, section 5. */
export const TYPED_UNIT_DESIGNATOR_WORDS: ReadonlySet<string> = new Set([
  "APT", "APARTMENT", "UNIT", "STE", "SUITE", "RM", "ROOM", "FL", "FLOOR", "BLDG", "BUILDING",
]);

/** `plausibleValue` (PropertyDataProvider.swift:628), Swift's own comment kept: "Non-empty, short, and
 *  alphanumeric-ish. The length cap is what stops a missing comma turning 'Apt 2210 Philadelphia' into the unit
 *  'Philadelphia'."
 *
 *  `cleaned.count` is Characters, not UTF-16 units, and `allSatisfy` is over Characters too — so both go through
 *  `characters()` rather than through `String.length`. */
export function plausibleUnitValue(value: string): boolean {
  const cs = characters(value);
  const alphanumeric = (c: string): boolean => isSwiftLetter(c) || isSwiftNumber(c);
  const cleaned = cs.filter(alphanumeric);
  return cleaned.length > 0
    && cleaned.length <= 8
    && cs.every((c) => alphanumeric(c) || c === "-" || c === "#");
}

/** `AddressMatch.typedUnitDesignator(in:)` (PropertyDataProvider.swift:624): the unit designator the typed text names,
 *  AS THE ORIGINAL SUBSTRING — "Unit 1512", "Apt 2210", "#704" — in the owner's own spelling and casing.
 *
 *  ONLY THE FIRST COMMA SEGMENT IS SEARCHED, which is Swift's rule and its reason: "That is where a unit lives;
 *  scanning the whole string would let a city or state token stand in for a unit value." */
export function typedUnitDesignator(typed: string): string | null {
  const comma = typed.indexOf(",");
  const street = trimmed(comma < 0 ? typed : typed.slice(0, comma));
  const tokens = street.split(" ").filter((t) => t.length > 0);
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const up = uppercased(token);
    const next = tokens[i + 1];
    // "#2210" — the designator and its value in one token.
    if (up.startsWith("#") && plausibleUnitValue(characters(token).slice(1).join(""))) return token;
    // "# 2210" — punctuation, then the value.
    if (up === "#" && next !== undefined && plausibleUnitValue(next)) return `#${next}`;
    if (TYPED_UNIT_DESIGNATOR_WORDS.has(up) && next !== undefined && plausibleUnitValue(next)) return `${token} ${next}`;
  }
  return null;
}

/** Where the unit the appeal uses came from. `ownerField` is the owner's own typing in the unit field; `addressLine` is
 *  R115's lift out of the typed address. */
export type UnitSource = "ownerField" | "addressLine";

export interface UnitInForce {
  /** The unit field's value. The owner's VERBATIM text when they filled it, and the bare token of the designator the
   *  address line names when they did not — "Unit 1512" lifts as "1512", the way `SubjectProperty.bareUnitToken`
   *  reduces a designator, so what lands in the field reads like a unit field and not like an address fragment. */
  readonly value: string;
  readonly source: UnitSource;
}

/** THE UNIT THE APPEAL USES, AND THE ONE WRITER OF IT (R115).
 *
 *  Every reader derives from here rather than from `request.unit`: G-4, the address body the pre-spend confirmation
 *  renders, the subject the record phase builds, and through G-4 the charge gate. The event still freezes the owner's
 *  own inputs verbatim, so the lift is always re-derivable from what they actually sent and never overwrites the
 *  record of it. */
export function unitInForce(typedAddress: string, unitField: string | null): UnitInForce | null {
  if (trimmed(unitField ?? "").length > 0) return { value: unitField as string, source: "ownerField" };
  const designator = typedUnitDesignator(typedAddress);
  if (designator === null) return null;
  const value = bareUnitToken(designator);
  return value === null ? null : { value, source: "addressLine" };
}
